import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    name: String,
    autoStart: { type: Boolean, default: false }
  }

  async connect() {
    const { default: Shepherd } = await import("shepherd.js")
    this.Shepherd = Shepherd
    this.tour = this.createTour()

    if (this.autoStartValue || this.shouldShowTour()) {
      setTimeout(() => this.start(), 500)
    }
  }

  start() {
    if (this.tour) {
      this.tour.start()
    }
  }

  createTour() {
    const tourName = this.nameValue || 'default'
    const tourConfig = this.getTourConfig(tourName)

    const tour = new this.Shepherd.Tour({
      useModalOverlay: false,
      defaultStepOptions: {
        classes: 'shepherd-theme-custom',
        scrollTo: { behavior: 'smooth', block: 'center' },
        cancelIcon: {
          enabled: true
        }
      }
    })

    const totalSteps = tourConfig.steps.length

    // Add progress dots to every step
    if (totalSteps > 1) {
      tourConfig.steps.forEach((step, index) => {
        const dots = Array.from({ length: totalSteps }, (_, i) =>
          `<span class="shepherd-dot${i === index ? ' active' : ''}"></span>`
        ).join('')
        step.text = (step.text || '') + `<div class="shepherd-dots">${dots}</div>`
      })
    }

    // Add "Don't show again" checkbox to the last step (after dots)
    if (totalSteps > 0) {
      const lastStep = tourConfig.steps[totalSteps - 1]
      lastStep.text = lastStep.text +
        '<div class="shepherd-dont-show-wrapper">' +
        '<label class="shepherd-dont-show-label">' +
        '<input type="checkbox" checked class="shepherd-dont-show-again"> Don\'t show this tour again' +
        '</label></div>'
    }

    // Add "Skip tour" button to all intermediate steps (not welcome, not last)
    tourConfig.steps.forEach((step, index) => {
      if (index > 0 && index < totalSteps - 1) {
        step.buttons = [
          {
            text: 'Skip tour',
            action: function() { return this.cancel() },
            classes: 'shepherd-button-skip'
          },
          ...(step.buttons || [])
        ]
      }
      tour.addStep(step)
    })

    // Fix: Floating UI keeps re-overriding position with inline !important styles.
    // Use a MutationObserver to re-apply our fix whenever Floating UI re-runs.
    let positionObserver = null

    const clampElement = (el) => {
      if (el.classList.contains('shepherd-centered')) {
        el.style.setProperty('left', '50%', 'important')
        el.style.setProperty('top', '50%', 'important')
        el.style.setProperty('transform', 'translate(-50%, -50%)', 'important')
        el.style.setProperty('margin', '0', 'important')
      } else {
        const pad = 16
        const rect = el.getBoundingClientRect()
        const vw = window.innerWidth
        const vh = window.innerHeight
        if (rect.right > vw - pad) {
          el.style.setProperty('left', `${Math.max(pad, rect.left - (rect.right - vw + pad))}px`, 'important')
        }
        if (rect.left < pad) {
          el.style.setProperty('left', `${pad}px`, 'important')
        }
        if (rect.bottom > vh - pad) {
          el.style.setProperty('top', `${Math.max(pad, rect.top - (rect.bottom - vh + pad))}px`, 'important')
        }
      }
    }

    tour.on('show', () => {
      if (positionObserver) positionObserver.disconnect()

      setTimeout(() => {
        const el = document.querySelector('.shepherd-element')
        if (!el) return

        clampElement(el)

        // Watch for Floating UI re-overrides and immediately re-clamp
        let applying = false
        positionObserver = new MutationObserver(() => {
          if (applying) return
          applying = true
          requestAnimationFrame(() => {
            const target = document.querySelector('.shepherd-element')
            if (target) clampElement(target)
            applying = false
          })
        })
        positionObserver.observe(el, { attributes: true, attributeFilter: ['style'] })
      }, 50)
    })

    tour.on('hide', () => {
      if (positionObserver) { positionObserver.disconnect(); positionObserver = null }
    })

    // Mark tour as completed when finished (respects "Don't show again" checkbox)
    tour.on('complete', () => {
      if (this.shouldRemember()) {
        this.markTourCompleted(tourName)
      }
    })

    tour.on('cancel', () => {
      this.markTourCompleted(tourName)
    })

    return tour
  }

  getTourConfig(name) {
    const tours = {
      'pick_list_creation': this.pickListCreationTour(),
      'inventory_receiving': this.inventoryReceivingTour(),
      'order_fulfillment': this.orderFulfillmentTour(),
      'simple_mode_dashboard': this.simpleModeDashboardTour(),
      'simple_mode_order_creation': this.simpleModeOrderCreationTour(),
      'simple_mode_stock_management': this.simpleModeStockManagementTour(),
      'suppliers': this.suppliersTour(),
      'purchase_orders': this.purchaseOrdersTour(),
      'marketplace': this.marketplaceTour(),
      'cycle_counts': this.cycleCountsTour(),
      'lots': this.lotsTour()
    }

    return tours[name] || { steps: [] }
  }

  pickListCreationTour() {
    return {
      steps: [
        {
          id: 'welcome',
          title: 'Create Your First Pick List',
          text: 'Let me show you how to create a pick list for order fulfillment. This will take about 2 minutes.',
          buttons: [
            {
              text: 'Skip',
              action: function() {
                return this.cancel()
              },
              secondary: true
            },
            {
              text: 'Start Tour',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'order-selection',
          title: 'Select an Order',
          text: 'Pick lists are generated from pending orders. Choose an order that needs to be fulfilled.',
          attachTo: {
            element: '.order-list',
            on: 'bottom'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'warehouse-selection',
          title: 'Choose Warehouse',
          text: 'Select which warehouse will fulfill this order. The system will check if inventory is available.',
          attachTo: {
            element: '[name="warehouse_id"]',
            on: 'bottom'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'picking-strategy',
          title: 'Picking Strategy',
          text: 'Choose FIFO (First In, First Out) or FEFO (First Expired, First Out) for lot-tracked items. FIFO is recommended for most cases.',
          attachTo: {
            element: '[name="strategy"]',
            on: 'top'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'complete',
          title: 'You\'re Ready!',
          text: 'Click "Create Pick List" to generate the pick list. You can then assign it to a picker and track progress in real-time.',
          buttons: [
            {
              text: 'Got it!',
              action: function() {
                return this.complete()
              }
            }
          ]
        }
      ]
    }
  }

  inventoryReceivingTour() {
    return {
      steps: [
        {
          id: 'welcome',
          title: 'Receive Inventory',
          text: 'Learn how to receive incoming shipments and stock your warehouse.',
          buttons: [
            {
              text: 'Skip',
              action: function() {
                return this.cancel()
              },
              secondary: true
            },
            {
              text: 'Start',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'shipment-selection',
          title: 'Select Shipment',
          text: 'Choose a pending shipment to receive. You can see the expected quantity and product details.',
          attachTo: {
            element: '.shipment-list',
            on: 'bottom'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'location-assignment',
          title: 'Assign Location',
          text: 'Choose where to store this inventory in your warehouse. Use the location picker to navigate through aisles, racks, and bins.',
          attachTo: {
            element: '[name="location_id"]',
            on: 'bottom'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'lot-tracking',
          title: 'Lot Information',
          text: 'Enter the lot number and expiration date if applicable. This helps with traceability and FIFO/FEFO picking.',
          attachTo: {
            element: '[name="lot_number"]',
            on: 'top'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Finish',
              action: function() {
                return this.complete()
              }
            }
          ]
        }
      ]
    }
  }

  orderFulfillmentTour() {
    return {
      steps: [
        {
          id: 'welcome',
          title: 'Order Fulfillment Workflow',
          text: 'Let me show you the complete order fulfillment process from creation to shipping.',
          buttons: [
            {
              text: 'Skip',
              action: function() {
                return this.cancel()
              },
              secondary: true
            },
            {
              text: 'Begin',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'create-order',
          title: 'Create Order',
          text: 'Start by creating a new order. Select a customer and add products to the order.',
          attachTo: {
            element: '.btn-create-order',
            on: 'bottom'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'generate-picklist',
          title: 'Generate Pick List',
          text: 'Once the order is confirmed, create a pick list to start fulfillment.',
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'pick-pack-ship',
          title: 'Pick, Pack, Ship',
          text: 'Assign the pick list to a picker, track picking progress, pack the order, and mark it as shipped.',
          buttons: [
            {
              text: 'Got it!',
              action: function() {
                return this.complete()
              }
            }
          ]
        }
      ]
    }
  }

  shouldShowTour() {
    const tourName = this.nameValue
    if (!tourName) return false

    // Check if user has already seen this tour
    const completedTours = this.getCompletedTours()
    return !completedTours.includes(tourName)
  }

  getCompletedTours() {
    const tours = localStorage.getItem('completed_tours')
    return tours ? JSON.parse(tours) : []
  }

  shouldRemember() {
    const checkbox = document.querySelector('.shepherd-dont-show-again')
    return checkbox ? checkbox.checked : true
  }

  markTourCompleted(tourName) {
    const completedTours = this.getCompletedTours()
    if (!completedTours.includes(tourName)) {
      completedTours.push(tourName)
      localStorage.setItem('completed_tours', JSON.stringify(completedTours))
    }
  }

  simpleModeDashboardTour() {
    return {
      steps: [
        {
          id: 'welcome',
          title: 'Welcome to Simple Mode!',
          text: 'Let me show you around your streamlined warehouse dashboard. This tour will take about 1 minute.',
          buttons: [
            {
              text: 'Skip',
              action: function() {
                return this.cancel()
              },
              secondary: true
            },
            {
              text: 'Show Me',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'quick-actions',
          title: 'Quick Actions',
          text: 'Start here for your most common tasks: creating orders, managing products, and viewing customers.',
          attachTo: {
            element: '.card:has(h2:contains("Quick Actions"))',
            on: 'bottom'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'key-metrics',
          title: 'Key Metrics',
          text: 'Monitor your warehouse at a glance: pending orders, total products, active customers, and stock levels.',
          attachTo: {
            element: '[style*="grid-template-columns"]',
            on: 'top'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'low-stock',
          title: 'Low Stock Alerts',
          text: 'Keep an eye on products running low. Click "Restock" to quickly add inventory.',
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Finish',
              action: function() {
                return this.complete()
              }
            }
          ]
        }
      ]
    }
  }

  simpleModeOrderCreationTour() {
    return {
      steps: [
        {
          id: 'welcome',
          title: 'Create Your First Order',
          text: 'Creating orders in Simple Mode is easy! Just 3 simple steps.',
          buttons: [
            {
              text: 'Skip',
              action: function() {
                return this.cancel()
              },
              secondary: true
            },
            {
              text: 'Start',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'step-1',
          title: 'Step 1: Choose Customer',
          text: 'Select an existing customer or create a new one. Simple Mode handles all the warehouse details automatically.',
          attachTo: {
            element: '[data-simple-order-wizard-step="1"]',
            on: 'right'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'step-2',
          title: 'Step 2: Add Products',
          text: 'Select products and quantities. The order total updates automatically as you add items.',
          attachTo: {
            element: '[data-simple-order-wizard-step="2"]',
            on: 'right'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'step-3',
          title: 'Step 3: Review & Submit',
          text: 'Review your order and submit. Simple Mode will automatically create the pick list and invoice.',
          attachTo: {
            element: '[data-simple-order-wizard-step="3"]',
            on: 'right'
          },
          buttons: [
            {
              text: 'Got it!',
              action: function() {
                return this.complete()
              }
            }
          ]
        }
      ]
    }
  }

  simpleModeStockManagementTour() {
    return {
      steps: [
        {
          id: 'welcome',
          title: 'Simple Stock Management',
          text: 'Adding and removing stock is straightforward. Let me show you how.',
          buttons: [
            {
              text: 'Skip',
              action: function() {
                return this.cancel()
              },
              secondary: true
            },
            {
              text: 'Show Me',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'quick-actions',
          title: 'Quick Actions',
          text: 'Use these buttons to quickly add or remove stock from your inventory.',
          attachTo: {
            element: '.card:has(h2:contains("Quick Actions"))',
            on: 'bottom'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'product-table',
          title: 'All Products',
          text: 'See your current stock levels at a glance. Use the add/remove buttons on each row for quick adjustments.',
          attachTo: {
            element: '.table',
            on: 'top'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Finish',
              action: function() {
                return this.complete()
              }
            }
          ]
        }
      ]
    }
  }

  suppliersTour() {
    return {
      steps: [
        {
          id: 'welcome',
          title: 'Manage Your Suppliers',
          text: 'Track your suppliers, their contact details, and the products they provide. Let me show you around.',
          buttons: [
            {
              text: 'Skip',
              action: function() {
                return this.cancel()
              },
              secondary: true
            },
            {
              text: 'Start',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'supplier-list',
          title: 'Supplier Directory',
          text: 'View all your suppliers in one place. See contact info, product counts, and recent activity at a glance.',
          attachTo: {
            element: '.table',
            on: 'top'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'add-supplier',
          title: 'Add a Supplier',
          text: 'Click here to add a new supplier. You can store their contact details, lead times, and link them to products.',
          attachTo: {
            element: '[href*="suppliers/new"], .btn-create-supplier',
            on: 'bottom'
          },
          buttons: [
            {
              text: 'Got it!',
              action: function() {
                return this.complete()
              }
            }
          ]
        }
      ]
    }
  }

  purchaseOrdersTour() {
    return {
      steps: [
        {
          id: 'welcome',
          title: 'Purchase Orders',
          text: 'Create and manage purchase orders to replenish your inventory from suppliers.',
          buttons: [
            {
              text: 'Skip',
              action: function() {
                return this.cancel()
              },
              secondary: true
            },
            {
              text: 'Start',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'po-list',
          title: 'Your Purchase Orders',
          text: 'See all purchase orders with their status, supplier, and expected delivery dates.',
          attachTo: {
            element: '.table',
            on: 'top'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'create-po',
          title: 'Create a Purchase Order',
          text: 'Click here to create a new PO. Select a supplier, add products and quantities, then send it directly to your supplier.',
          attachTo: {
            element: '[href*="purchase_orders/new"], .btn-create-po',
            on: 'bottom'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'po-statuses',
          title: 'PO Workflow',
          text: 'Purchase orders flow through Draft → Sent → Approved → Received. You can track each step and mark items as received when they arrive.',
          buttons: [
            {
              text: 'Got it!',
              action: function() {
                return this.complete()
              }
            }
          ]
        }
      ]
    }
  }

  marketplaceTour() {
    return {
      steps: [
        {
          id: 'welcome',
          title: 'Your Marketplace',
          text: 'Set up your online marketplace to sell products directly to customers and resellers.',
          buttons: [
            {
              text: 'Skip',
              action: function() {
                return this.cancel()
              },
              secondary: true
            },
            {
              text: 'Start',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'marketplace-profile',
          title: 'Company Profile',
          text: 'Customize your public marketplace profile. Add your logo, description, and contact details so customers can find you.',
          // Anchored to the actual Profile Information section in
          // app/views/settings/marketplace.html.erb (data-tour-step="profile").
          // Was '.card:first-of-type' which matched nothing on this page,
          // causing the tooltip to float dead-center. Shepherd's scrollTo
          // (configured globally in createTour) now brings the section
          // into view as soon as the step opens.
          attachTo: {
            element: '[data-tour-step="profile"]',
            on: 'top'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'product-visibility',
          title: 'Catalog Visibility',
          text: 'Control who can view your catalog — open to the public, unlisted (link only), or restricted to verified customers.',
          attachTo: {
            element: '[data-tour-step="visibility"]',
            on: 'top'
          },
          buttons: [
            {
              text: 'Got it!',
              action: function() {
                return this.complete()
              }
            }
          ]
        }
      ]
    }
  }

  cycleCountsTour() {
    return {
      steps: [
        {
          id: 'welcome',
          title: 'Cycle Counts',
          text: 'Keep your inventory accurate with regular cycle counts. Count a subset of items on a rolling basis instead of doing full stock takes.',
          buttons: [
            {
              text: 'Skip',
              action: function() {
                return this.cancel()
              },
              secondary: true
            },
            {
              text: 'Start',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'count-list',
          title: 'Count History',
          text: 'View all your cycle counts and their statuses. Track variances between expected and actual quantities.',
          attachTo: {
            element: '.table',
            on: 'top'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'create-count',
          title: 'Start a Count',
          text: 'Create a new cycle count by selecting a warehouse location. Assign it to a team member, count the items, and reconcile any differences.',
          attachTo: {
            element: '[href*="cycle_counts/new"], .btn-create-count',
            on: 'bottom'
          },
          buttons: [
            {
              text: 'Got it!',
              action: function() {
                return this.complete()
              }
            }
          ]
        }
      ]
    }
  }

  lotsTour() {
    return {
      steps: [
        {
          id: 'welcome',
          title: 'Lot Tracking',
          text: 'Track inventory by lot number for traceability, expiration management, and regulatory compliance.',
          buttons: [
            {
              text: 'Skip',
              action: function() {
                return this.cancel()
              },
              secondary: true
            },
            {
              text: 'Start',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'lot-list',
          title: 'Your Lots',
          text: 'See all lot numbers with their status, quantity, and expiration dates. Quickly identify lots that are expiring soon.',
          attachTo: {
            element: '.table',
            on: 'top'
          },
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'expiration-tracking',
          title: 'Expiration Tracking',
          text: 'Set expiration dates on lots to get automatic alerts before products expire. Keep your inventory fresh and compliant.',
          buttons: [
            {
              text: 'Back',
              action: function() {
                return this.back()
              },
              secondary: true
            },
            {
              text: 'Next',
              action: function() {
                return this.next()
              }
            }
          ]
        },
        {
          id: 'fefo-explanation',
          title: 'FEFO Picking',
          text: 'With lot tracking enabled, pick lists can use FEFO (First Expired, First Out) to automatically prioritize items closest to expiration.',
          buttons: [
            {
              text: 'Got it!',
              action: function() {
                return this.complete()
              }
            }
          ]
        }
      ]
    }
  }

  disconnect() {
    if (this.tour) {
      this.tour.cancel()
    }
  }
}
